import { OrderSyncTriggerEventModel } from 'lib/model/objects/orderSyncTriggerEventModel';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { useFirestoreQueryListener } from 'lib/frontend/hooks/useFirestoreQueryListener';
import { mapAsync } from 'lib/helpers/array/async';
import { getModelFromRef, getModelFromSnapshot } from 'lib/model';
import { SyncAttemptModel } from 'lib/model/objects/syncAttemptModel';
import { safeStringify } from 'lib/utils/stringify';
import { selectAvailableOrganizations, selectIsPublisher } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { getFirebaseContext } from 'utils/firebase';
import { ERef } from 'lib/types';
import { Order } from 'lib/types/order';
import { Product } from 'lib/enums/Product';
import {
  CLASSIFIED_DETAIL_ROUTES,
  OBITUARY_DETAIL_ROUTES
} from 'router/routes';

export type SyncAttemptWithTriggerEvent = {
  syncAttempt: SyncAttemptModel;
  triggerEvent: OrderSyncTriggerEventModel;
};

export const ProductToRoutesMap: Record<
  Product,
  typeof CLASSIFIED_DETAIL_ROUTES | typeof OBITUARY_DETAIL_ROUTES | null
> = {
  [Product.Classified]: CLASSIFIED_DETAIL_ROUTES,
  [Product.Obituary]: OBITUARY_DETAIL_ROUTES,
  [Product.Notice]: null
};

export const useSyncAttempts = (
  order: ERef<Order>
): SyncAttemptWithTriggerEvent[] => {
  const availablePublishers = useAppSelector(selectAvailableOrganizations);
  const isPublisher = useAppSelector(selectIsPublisher);

  const query = getFirebaseContext()
    .syncAttemptsRef()
    .where('order', '==', order)
    .orderBy('createdAt', 'desc');

  const syncAttempts = useFirestoreQueryListener(query, [order.id]);

  const { value } = useAsyncEffect({
    fetchData: async () => {
      if (!isPublisher || !syncAttempts || syncAttempts.empty) {
        return [];
      }

      const relevantPublishers = new Set(
        availablePublishers.map(publisher => publisher.id)
      );

      const relevantSyncAttempts = syncAttempts.docs
        .filter(attempt => relevantPublishers.has(attempt.data().publisher.id))
        .map(attempt =>
          getModelFromSnapshot(SyncAttemptModel, getFirebaseContext(), attempt)
        );

      const [error, attemptsWithEvent] = await mapAsync(
        relevantSyncAttempts,
        async syncAttempt => {
          const triggerEvent = await getModelFromRef(
            OrderSyncTriggerEventModel,
            getFirebaseContext(),
            syncAttempt.modelData.triggerEvent
          );

          return {
            syncAttempt,
            triggerEvent
          };
        }
      );

      if (error) {
        throw error;
      }

      return attemptsWithEvent;
    },
    dependencies: [safeStringify(syncAttempts)]
  });

  return value ?? [];
};
